import { ref, computed, onBeforeUnmount } from 'vue';
import { VoiceAnalysisStatus } from '@/api/openapi';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import { useAutoRetry } from '@/composable/use-auto-retry';
import type { VoiceAnalysisManagerOptions } from './voice-analysis-types';

const POLLING_INTERVAL_MS = 45000; // 45秒間隔
const MAX_POLLING_RETRIES = 28; // 28回（45秒 × 28回 = 21分）
const STOP_REASON_MAX_RETRIES = 'max retries reached';

/**
 * 自動分析専用の音声分析マネージャー
 * - Zoom Phone 連携で自動的に開始
 * - バナー表示なし
 * - transcription_statuses API で id のみ指定してポーリング（バックエンドは execute_method を受け取らない）
 */
export const useAutoVoiceAnalysis = (options?: VoiceAnalysisManagerOptions) => {
  const analyzingIds = ref<Set<number>>(new Set());
  const sharedPoller = ref<{ stop: () => void; restart: () => void } | null>(null);

  const api = new ZoomPhoneResultApiService();

  // ============================================================================
  // 内部関数
  // ============================================================================

  const completeAnalysis = (
    id: number,
    status: VoiceAnalysisStatus,
  ) => {
    analyzingIds.value.delete(id);
    options?.onStatusUpdate?.(id, status);
  };

  const processPollingResults = (
    currentIds: number[],
    statuses: { id: number; voiceAnalysisStatus: VoiceAnalysisStatus }[],
  ): boolean => {
    let allCompleted = true;

    for (const id of currentIds) {
      const result = statuses.find((r) => r.id === id);

      if (!result) {
        allCompleted = false;
        continue;
      }

      const status = result.voiceAnalysisStatus;

      if (status === VoiceAnalysisStatus.Failed) {
        completeAnalysis(id, VoiceAnalysisStatus.Failed);
        continue;
      }

      if (status === VoiceAnalysisStatus.Succeeded) {
        completeAnalysis(id, VoiceAnalysisStatus.Succeeded);
        continue;
      }

      allCompleted = false;
    }

    return allCompleted;
  };

  /**
   * 自動分析用のポーリング（id のみでステータス取得）
   */
  const executePolling = async (): Promise<boolean> => {
    const currentIds = Array.from(analyzingIds.value);

    if (currentIds.length === 0) {
      return true;
    }

    const response = await api.transcriptionStatusesZoomPhoneResults({
      request: {
        id: currentIds,
      },
    });

    if (!response?.data) {
      return false;
    }

    const data = response.data;
    const statuses = data.statuses;
    if (!statuses) {
      return false;
    }

    return processPollingResults(currentIds, statuses);
  };

  const handlePollingError = () => {
    const currentIds = Array.from(analyzingIds.value);
    currentIds.forEach(id => {
      completeAnalysis(id, VoiceAnalysisStatus.Failed);
    });
    sharedPoller.value = null;
  };

  const handlePollingStop = (reason?: string) => {
    if (reason === STOP_REASON_MAX_RETRIES) {
      const currentIds = Array.from(analyzingIds.value);
      currentIds.forEach(id => {
        completeAnalysis(id, VoiceAnalysisStatus.Failed);
      });
    }
    sharedPoller.value = null;
  };

  const startSharedPolling = () => {
    if (sharedPoller.value) {
      sharedPoller.value.stop();
    }

    if (analyzingIds.value.size === 0) {
      sharedPoller.value = null;
      return;
    }

    const { start, stop } = useAutoRetry(
      executePolling,
      {
        interval: POLLING_INTERVAL_MS,
        maxRetries: MAX_POLLING_RETRIES,
        onSuccess: () => {
          sharedPoller.value = null;
        },
        onError: handlePollingError,
        onStop: handlePollingStop,
      },
    );

    sharedPoller.value = { stop, restart: startSharedPolling };
    void start();
  };

  // ============================================================================
  // 外部に提供される関数
  // ============================================================================

  /**
   * 自動分析の追跡を開始
   * - API呼び出しなし（バックエンドが既にタスク作成済み）
   * - ポーリングのみ開始
   */
  const trackAnalysis = (id: number, _calleeDidNumber?: string) => {
    // 重複防止
    if (analyzingIds.value.has(id)) {
      return;
    }

    analyzingIds.value.add(id);
    startSharedPolling();
  };

  const isAnalyzing = (id: number): boolean => {
    return analyzingIds.value.has(id);
  };

  const stopAll = () => {
    if (sharedPoller.value) {
      sharedPoller.value.stop();
      sharedPoller.value = null;
    }
    analyzingIds.value.clear();
  };

  onBeforeUnmount(() => {
    stopAll();
  });

  return {
    analyzingIds: computed(() => analyzingIds.value),
    trackAnalysis,
    isAnalyzing,
    stopAll,
  };
};
